import { useAppDispatch } from 'app/hooks';
import { useContextMenu } from 'components/common/context-menu-manager';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ApplianceItemType,
  ContextMenuItemType,
  EdgeDirectionType,
  GridSpaceDataType,
  ItemType,
  LevelType,
  PartialWithRequired,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  deleteUserSpacesAndRebuildLeftovers,
  endUndoBatch,
  updateStructureItem
} from 'features/structure';
import {
  addApplianceToSpace,
  addDoorToWall,
  addWindowToWall,
  moveSpaceToLevel
} from 'features/structure/structure-thunks';
import { setActiveItem } from 'features/ui';
import React, { useCallback, useEffect } from 'react';
import { computeOpenToToggle, isOpenTo } from 'utils/open-to';
import { OPENING_PRESETS } from 'utils/opening-drag';
import { EdgeRunType } from 'utils/opening-position-types';
import { spacePolygonsShareEdge } from 'utils/polygon-shared-edges';
import { findSpaceById } from 'utils/structure';
import { createActiveItem } from 'utils/ui';
import ChangeShapeSubmenu from './change-shape-submenu';

export type GridSpaceContextMenuTarget = 'space' | EdgeDirectionType;

export type GridSpaceMode =
  | 'spaces'
  | 'conditioning'
  | 'ceilings'
  | 'fenestration'
  | 'electrical'
  | 'summary'
  | 'static'
  | 'zones'
  | 'appliances';

type UseGridSpaceContextMenuParams = {
  gridSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  structure: LevelType[];
  selectedSpaceIds: Set<string>;
  mode: GridSpaceMode;
  significantAppliances: ApplianceItemType[];
  items: ItemType[];
  hasWallOnEdge: (edge: EdgeDirectionType) => boolean;
  contextMenuFilter?: (items: ContextMenuItemType[]) => ContextMenuItemType[];
  setActiveEdge: (edge: EdgeDirectionType | null) => void;
  setActiveSpace: (active: boolean) => void;
};

export function useGridSpaceContextMenu({
  gridSpace,
  allGridSpaces,
  structure,
  selectedSpaceIds,
  mode,
  significantAppliances,
  items,
  hasWallOnEdge,
  contextMenuFilter,
  setActiveEdge,
  setActiveSpace
}: UseGridSpaceContextMenuParams) {
  const dispatch = useAppDispatch();
  const {
    openContextMenu,
    closeContextMenu,
    contextMenu,
    updateContextMenuItems
  } = useContextMenu();

  const getContextMenuItems = useCallback(
    (
      target: GridSpaceContextMenuTarget,
      run?: EdgeRunType
    ): ContextMenuItemType[] => {
      const isMultiSelectContext =
        selectedSpaceIds.size > 1 && selectedSpaceIds.has(gridSpace.spaceId);
      const currentLevel = structure.find((level) =>
        level.sections.some((section) =>
          section.spaces.some((s) => s.uniqueId === gridSpace.spaceId)
        )
      );
      const otherLevels = structure.filter(
        (level) =>
          level.type === 'level' && level.uniqueId !== currentLevel?.uniqueId
      );

      if (target === 'space' && isMultiSelectContext) {
        return [
          {
            label: `Move ${selectedSpaceIds.size} Spaces`,
            submenu: otherLevels.map((level) => ({
              label: `to ${level.label}`,
              onClick: () => {
                Array.from(selectedSpaceIds).forEach((spaceId) => {
                  dispatch(
                    moveSpaceToLevel({ spaceId, levelId: level.uniqueId })
                  );
                });
              }
            })),
            disabled: otherLevels.length === 0
          },
          {
            separator: true
          },
          {
            label: `Delete ${selectedSpaceIds.size} Spaces`,
            onClick: () => {
              dispatch(
                deleteUserSpacesAndRebuildLeftovers(
                  Array.from(selectedSpaceIds)
                )
              );
            }
          }
        ];
      }

      if (target === 'space') {
        return [
          {
            label: 'Properties',
            onClick: () => {
              const structureSpace = findSpaceById(
                structure,
                gridSpace.spaceId
              );

              if (structureSpace) {
                dispatch(
                  setActiveItem(
                    createActiveItem({
                      type: 'space',
                      item: structureSpace
                    })
                  )
                );
              }
            }
          },
          {
            separator: true
          },
          {
            label: 'Change Shape',
            renderSubmenu: ({ onClose }) => (
              <ChangeShapeSubmenu
                spaceId={gridSpace.spaceId}
                onClose={onClose}
              />
            )
          },
          {
            label: 'Move',
            submenu: otherLevels.map((level) => ({
              label: `to ${level.label}`,
              onClick: () => {
                dispatch(
                  moveSpaceToLevel({
                    spaceId: gridSpace.spaceId,
                    levelId: level.uniqueId
                  })
                );
              }
            })),
            disabled: otherLevels.length === 0
          },
          (() => {
            const uniqueAdjacent = allGridSpaces.filter(
              (candidate) =>
                candidate.spaceId !== gridSpace.spaceId &&
                spacePolygonsShareEdge(
                  gridSpace,
                  candidate,
                  STRUCTURE_GRID_SIZE
                )
            );
            const currentSpace = findSpaceById(structure, gridSpace.spaceId);
            return {
              label: 'Open to',
              disabled: uniqueAdjacent.length === 0,
              submenu: uniqueAdjacent.map((adjSpace) => ({
                label: adjSpace.label,
                checked: currentSpace
                  ? isOpenTo(currentSpace, adjSpace.spaceId)
                  : false,
                keepOpen: true,
                onClick: () => {
                  const { aOpenTo, bOpenTo } = computeOpenToToggle(
                    structure,
                    gridSpace.spaceId,
                    adjSpace.spaceId
                  );
                  dispatch(beginUndoBatch());
                  dispatch(
                    updateStructureItem({
                      uniqueId: gridSpace.spaceId,
                      openTo: aOpenTo
                    })
                  );
                  dispatch(
                    updateStructureItem({
                      uniqueId: adjSpace.spaceId,
                      openTo: bOpenTo
                    })
                  );
                  dispatch(endUndoBatch());
                }
              }))
            };
          })(),
          ...(mode === 'appliances'
            ? [
                {
                  label: 'Appliances',
                  disabled: significantAppliances.length === 0,
                  submenu: significantAppliances.map((appliance) => ({
                    label: appliance.label,
                    onClick: () => {
                      dispatch(
                        addApplianceToSpace({
                          spaceId: gridSpace.spaceId,
                          applianceItemId: appliance.itemId
                        })
                      );
                    }
                  }))
                }
              ]
            : []),
          {
            separator: true
          },
          {
            label: 'Change Type',
            submenu: items
              .filter((item) => item.group === 'Space')
              .map((item) => ({
                label: item.label,
                onClick: () => {
                  const structureSpace = findSpaceById(
                    structure,
                    gridSpace.spaceId
                  );

                  if (structureSpace) {
                    dispatch(
                      updateStructureItem({
                        uniqueId: structureSpace.uniqueId,
                        itemId: item.id,
                        attributes: item.attributes,
                        label: item.label
                      } as PartialWithRequired<SpaceType, 'uniqueId'>)
                    );
                  }
                }
              }))
          },
          {
            separator: true
          },
          {
            label: 'Delete Space',
            onClick: () => {
              dispatch(
                deleteUserSpacesAndRebuildLeftovers([gridSpace.spaceId])
              );
            }
          }
        ];
      }

      if (!hasWallOnEdge(target)) {
        return [];
      }

      return [
        {
          label: 'Add Window',
          submenu: [
            {
              label: 'Small',
              onClick: () => {
                dispatch(
                  addWindowToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    windowType: 'small'
                  })
                );
              }
            },
            {
              label: 'Medium',
              onClick: () => {
                dispatch(
                  addWindowToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    windowType: 'medium'
                  })
                );
              }
            },
            {
              label: 'Large',
              onClick: () => {
                dispatch(
                  addWindowToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    windowType: 'large'
                  })
                );
              }
            }
          ]
        },
        {
          label: 'Add Door',
          submenu: [
            {
              label: 'Wooden Door',
              onClick: () => {
                dispatch(
                  addDoorToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    material: 'Wood'
                  })
                );
              }
            },
            {
              label: 'Metal Door',
              onClick: () => {
                dispatch(
                  addDoorToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    material: 'Metal'
                  })
                );
              }
            },
            {
              label: 'Pet Door',
              onClick: () => {
                dispatch(
                  addDoorToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    doorType: 'pet'
                  })
                );
              }
            },
            {
              label: 'Single Glass Door',
              onClick: () => {
                const glassDoor = OPENING_PRESETS['new-door-glass-single'];
                dispatch(
                  addWindowToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    fenestrationType: 'Sliding Glass Door',
                    widthOverride: glassDoor.widthFt,
                    heightOverride: glassDoor.heightFt
                  })
                );
              }
            },
            {
              label: 'Double Glass Door',
              onClick: () => {
                const glassDoor = OPENING_PRESETS['new-door-glass-double'];
                dispatch(
                  addWindowToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    fenestrationType: 'French Door',
                    widthOverride: glassDoor.widthFt,
                    heightOverride: glassDoor.heightFt
                  })
                );
              }
            },
            {
              label: 'Single Garage Door',
              onClick: () => {
                dispatch(
                  addDoorToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    doorType: 'garage',
                    garageSize: 'single'
                  })
                );
              }
            },
            {
              label: 'Double Garage Door',
              onClick: () => {
                dispatch(
                  addDoorToWall({
                    spaceId: gridSpace.spaceId,
                    edge: target,
                    run,
                    doorType: 'garage',
                    garageSize: 'double'
                  })
                );
              }
            }
          ]
        }
      ];
    },
    [
      dispatch,
      gridSpace,
      structure,
      selectedSpaceIds,
      hasWallOnEdge,
      mode,
      significantAppliances,
      items,
      allGridSpaces
    ]
  );

  useEffect(() => {
    if (contextMenu.sourceId !== `${gridSpace.id}-space`) {
      return;
    }
    let refreshedItems = getContextMenuItems('space');
    if (contextMenuFilter) {
      refreshedItems = contextMenuFilter(refreshedItems);
    }
    if (refreshedItems.length === 0) {
      closeContextMenu();
      return;
    }
    updateContextMenuItems(refreshedItems);
  }, [
    contextMenu.sourceId,
    gridSpace.id,
    getContextMenuItems,
    contextMenuFilter,
    updateContextMenuItems,
    closeContextMenu
  ]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, target: GridSpaceContextMenuTarget) => {
      e.preventDefault();
      e.stopPropagation();

      let menuItems = getContextMenuItems(target);

      if (contextMenuFilter) {
        menuItems = contextMenuFilter(menuItems);
      }

      if (menuItems.length === 0) {
        return;
      }

      if (target !== 'space') {
        setActiveEdge(target);
        setActiveSpace(false);
      } else {
        setActiveEdge(null);
        setActiveSpace(true);
      }

      openContextMenu(
        e.clientX,
        e.clientY,
        menuItems,
        `${gridSpace.id}-${target}`
      );
    },
    [
      openContextMenu,
      gridSpace.id,
      getContextMenuItems,
      contextMenuFilter,
      setActiveEdge,
      setActiveSpace
    ]
  );

  return {
    getContextMenuItems,
    handleContextMenu,
    closeContextMenu,
    openContextMenu,
    contextMenu
  };
}
